import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Download, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripExt = (n) => (n || "").replace(/\.(csv|xlsx|xls)$/i, "");
const cleanName = (n) => stripExt(n.trim()).replace(/[^A-Za-z0-9 _.\-()]/g, "").trim().slice(0, 120) || "leads";

const FormatToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border p-0.5" role="radiogroup">
    {[["csv", "CSV"], ["xlsx", "Excel"]].map(([v, label]) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        className={`px-3 py-1.5 text-sm rounded transition-colors ${value === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
        data-testid={`share-format-${v}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const RecipientList = ({ users, selected, toggle }) => (
  <div className="max-h-40 overflow-y-auto border rounded-md divide-y" data-testid="share-recipient-list">
    {users.length === 0 && <p className="p-3 text-sm text-muted-foreground">No portal users found</p>}
    {users.map((u) => (
      <label key={u.user_id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-muted/50">
        <Checkbox checked={selected.includes(u.user_id)} onCheckedChange={() => toggle(u.user_id)} data-testid={`share-recipient-${u.user_id}`} />
        <span className="text-sm font-medium">{u.name}</span>
        <span className="text-xs text-muted-foreground truncate">{u.email}</span>
      </label>
    ))}
  </div>
);

export const ShareUploadDialog = ({ upload, open, onOpenChange }) => {
  const [fileName, setFileName] = useState("");
  const [format, setFormat] = useState("csv");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !upload) return;
    setFileName(stripExt(upload.file_name));
    setFormat(/\.xlsx?$/i.test(upload.file_name) ? "xlsx" : "csv");
    setSelected([]);
    fetch(`${API}/users/recipients`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [open, upload]);

  const finalName = `${cleanName(fileName)}.${format}`;

  const download = async () => {
    setBusy("download");
    try {
      const qs = new URLSearchParams({ file_name: fileName, format });
      const res = await fetch(`${API}/uploads/${upload.upload_id}/download?${qs}`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = finalName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${finalName}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const sendEmail = async () => {
    setBusy("email");
    try {
      const res = await fetch(`${API}/uploads/${upload.upload_id}/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: selected, file_name: fileName, format }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed to send email");
      toast.success(data.message);
      setSelected([]);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Download or email file</DialogTitle>
          <DialogDescription>Rename the file, pick a format, then download it or email a secure link.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">File name</label>
            <div className="flex gap-2 items-center">
              <Input value={fileName} onChange={(e) => setFileName(e.target.value)} maxLength={120} data-testid="share-file-name-input" />
              <FormatToggle value={format} onChange={setFormat} />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="share-final-name">Saved as: {finalName}</p>
          </div>

          <Button onClick={download} disabled={!!busy} className="w-full" data-testid="share-download-btn">
            {busy === "download" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download {finalName}
          </Button>

          <div className="space-y-2 pt-2 border-t">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email to portal users</label>
            <RecipientList users={users} selected={selected} toggle={toggle} />
            <p className="text-xs text-muted-foreground">Recipients get a download link that works for 7 days.</p>
            <Button variant="outline" onClick={sendEmail} disabled={!!busy || selected.length === 0} className="w-full" data-testid="share-email-btn">
              {busy === "email" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Email to {selected.length || "selected"} user{selected.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
